// Must stay first: third-party modules below read window storage as they load.
import './utils/installStorageFallback'
import React from 'react'
import { createRoot } from 'react-dom/client'
import MatchScreenWrapper from './MatchScreenWrapper'
import { MatchScreenDatasetAttributes } from './context/MatchScreenContext'
import { OptimizelyProviderInstance } from './context/OptimizelyContext'
import { registerRedesignDecision } from './redesign/registerRedesignDecision'
import { readMatchScreenDatasetAttributes } from './utils/datasetAttributes'
import { registerIframeResizerChild } from './utils/registerIframeResizerChild'
import { registerPersonalizationCopy } from './redesign/personalization/registerPersonalizationCopy'
import { registerSuperProperties } from './utils/tracking/mixpanel'
import { setOptimizelyPreviewMode } from './utils/tracking/optimizely'
import { CoralogixRum } from '@coralogix/browser'

/**
 * Main entry point for the match screen application
 */
const renderMatchScreen = () => {
  let rootElement = document.getElementById('match-screen-root')

  if (!rootElement) {
    rootElement = document.createElement('div')
    rootElement.id = 'match-screen-root'
    const currentScript = document.currentScript
    if (currentScript) {
      currentScript.insertAdjacentElement('afterend', rootElement)
    } else {
      console.error('Could not find match screen')
    }
  }

  const attributeData: MatchScreenDatasetAttributes = readMatchScreenDatasetAttributes(rootElement)

  registerIframeResizerChild()

  if (enableCoralogix && coralogixRumKey) {
    const fallbackVersion = '2.1.1'
    const version = typeof process !== 'undefined' && process.env ? (process.env.VERSION || fallbackVersion) : fallbackVersion

    CoralogixRum.init({
      application: 'funnel-ui-rum',
      version,
      public_key: coralogixRumKey,
      coralogixDomain: 'US2',
      labels: {
        page: 'match-screen'
      },
      sessionConfig: {
        sessionSampleRate: 20, // Percentage of overall sessions being tracked
        alwaysTrackSessionsWithErrors: false
      },
      sessionRecordingConfig: {
        enable: true, // Must declare.
        /**
         * If autoStartSessionRecording is false, you can manually start & stop your session recording.
         * Refer to Recording Manually Section.
         **/
        autoStartSessionRecording: true, // Automatically records your session when SDK is up.
        recordConsoleEvents: true, // Will record all console events from dev tools. Levels: log, debug, warn, error, info, table etc..
        sessionRecordingSampleRate: 20 // Percentage of overall sessions recording being tracked
      }
    })
  }

  // Admin advisor previews tag every Mixpanel event with
  // preview: true so dashboards can filter them out. Registered as a super
  // property before the redesign/personalization registrations and before
  // render, so the very first tracked events already carry it. The property
  // is intentionally absent (not false) on live match screens. Optimizely
  // conversion events have no such tag, so they are suppressed outright.
  if (attributeData.isPreview) {
    // persistent: false — the default persists to localStorage, which would tag
    // this browser's LIVE match-screen events preview:true after one preview.
    registerSuperProperties({ preview: true }, { persistent: false })
    setOptimizelyPreviewMode(true)
  }

  // FSM-1076: register before render so the first tracked events (page load
  // etc.) already carry the redesign variation super property.
  // Skipped in preview — with ?variant= the server decision can
  // differ from what renders, and preview events are isolated by the
  // preview:true super property anyway.
  if (!attributeData.isPreview) {
    registerRedesignDecision()
  }

  // Same timing for the personalization copy-group super properties. Eligibility and answers
  // derivation live in buildPersonalizationContext, shared with the render path.
  registerPersonalizationCopy(attributeData)

  const root = createRoot(rootElement)

  root.render(
    <React.StrictMode>
      <OptimizelyProviderInstance attributes={attributeData}>
        <MatchScreenWrapper attributes={attributeData} />
      </OptimizelyProviderInstance>
    </React.StrictMode>
  )
}

// Initialize the application when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderMatchScreen)
} else {
  renderMatchScreen()
}
